import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, ClipboardCheck, Copy, AlertTriangle, CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AI_PROMPT, KIND_META } from "@/pages/importer/importFormats";
import { ImportPreview } from "@/pages/importer/ImportPreview";

const TABS = ["procedures", "surgeons", "anesthesiologists"];

export default function ImportPage() {
  const { activeEmpresa, empresas } = useAuth();
  const [kind, setKind] = useState("procedures");
  const [text, setText] = useState({ procedures: "", surgeons: "", anesthesiologists: "" });
  const [result, setResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);

  const meta = KIND_META[kind];
  const readOnly = activeEmpresa === "all";
  const empresaName = empresas?.find((e) => e.id === activeEmpresa)?.name || "";

  function switchTab(k) {
    setKind(k);
    setResult(null);
  }

  async function run(dryRun) {
    const body = { kind, text: text[kind], dry_run: dryRun, update_existing: updateExisting };
    if (!body.text.trim()) { toast.error("Cole a lista antes de continuar"); return; }
    setBusy(true);
    try {
      const r = await api.post("/import/bulk", body);
      setResult(r.data);
      const s = r.data.summary;
      if (dryRun) {
        toast.success(`${s.to_create} novo(s), ${s.to_update} atualização(ões), ${s.duplicates} repetido(s), ${s.errors} erro(s)`);
      } else {
        toast.success(`Importado: ${s.created} criado(s), ${s.updated} atualizado(s)`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  function copyPrompt() {
    navigator.clipboard.writeText(AI_PROMPT);
    toast.success("Prompt copiado — cole no seu assistente de IA");
  }

  const s = result?.summary;
  const canImport = !!s && s.dry_run && (s.to_create > 0 || s.to_update > 0);

  return (
    <div className="space-y-5" data-testid="import-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Importar em massa</h1>
          <p className="text-sm text-slate-500">
            Cole a lista e cadastre de uma vez na empresa ativa{empresaName ? ` — ${empresaName}` : ""}.
          </p>
        </div>
        <Button variant="outline" onClick={copyPrompt} data-testid="import-copy-prompt">
          <Copy className="h-4 w-4 mr-2" /> Copiar prompt para IA
        </Button>
      </div>

      {readOnly && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
             data-testid="import-all-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Selecione uma empresa específica no seletor do topo — o modo “Todas as empresas” é somente leitura.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="import-tabs">
        {TABS.map((k) => (
          <button
            key={k}
            onClick={() => switchTab(k)}
            data-testid={`import-tab-${k}`}
            className={`rounded-full px-4 py-2 text-sm transition-colors ${
              kind === k ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {KIND_META[k].label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 space-y-1">
            <p className="font-medium text-slate-800">Formato — {meta.format}</p>
            <pre className="whitespace-pre-wrap font-mono text-xs text-slate-700">{meta.example}</pre>
            <p className="text-xs text-slate-500">{meta.hint}</p>
          </div>

          <Textarea
            rows={12}
            value={text[kind]}
            onChange={(e) => { setText({ ...text, [kind]: e.target.value }); setResult(null); }}
            placeholder={meta.example}
            className="font-mono text-sm"
            data-testid="import-textarea"
          />

          <div className="flex flex-wrap items-center gap-3">
            {kind === "procedures" && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={updateExisting}
                  onCheckedChange={(v) => { setUpdateExisting(!!v); setResult(null); }}
                  data-testid="import-update-existing"
                />
                Atualizar valores dos procedimentos já cadastrados
              </label>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => run(true)} disabled={busy || readOnly}
                    data-testid="import-validate-btn">
              <ClipboardCheck className="h-4 w-4 mr-2" /> Validar
            </Button>
            <Button onClick={() => run(false)} disabled={busy || readOnly || !canImport}
                    className="bg-blue-700 hover:bg-blue-800" data-testid="import-commit-btn">
              <Upload className="h-4 w-4 mr-2" /> Importar
            </Button>
          </div>
        </CardContent>
      </Card>

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="import-summary">
          <SummaryCard icon={CheckCircle2} tone="text-emerald-600" label={s.dry_run ? "Novos" : "Criados"}
                       value={s.dry_run ? s.to_create : s.created} testid="import-count-new" />
          <SummaryCard icon={ClipboardCheck} tone="text-blue-600" label={s.dry_run ? "A atualizar" : "Atualizados"}
                       value={s.dry_run ? s.to_update : s.updated} testid="import-count-update" />
          <SummaryCard icon={MinusCircle} tone="text-slate-400" label="Repetidos"
                       value={s.duplicates} testid="import-count-duplicate" />
          <SummaryCard icon={XCircle} tone="text-red-600" label="Erros"
                       value={s.errors} testid="import-count-error" />
        </div>
      )}

      {result?.rows?.length > 0 && <ImportPreview kind={kind} rows={result.rows} />}
    </div>
  );
}

function SummaryCard({ icon: Icon, tone, label, value, testid }) {
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className={`h-5 w-5 ${tone}`} />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
          <p className="text-lg font-semibold text-slate-900" data-testid={testid}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
